import type { TFunction } from "i18next"
import type { InsuranceStatus } from "@/types/user"

// https://www.sodra.lt/lt/situacijos/imoku-tarifai-savarankiskai-dirbantiems
// https://www.sodra.lt/lt/situacijos/informacija-draudejams/noriu-sumoketi-imokas/vykdau-individualia-veikla
// https://www.sodra.lt/skaiciuokles/individualios_veiklos_skaiciuokle
// https://www.vmi.lt/evmi/pajamu-apmokestinimas-deklaravimas#kaip-apmokestinamos-individualios-veiklos-pajamos

// GPM calculation uses credit system: GPM = (profit × rate) – PMK
// Official VMI formulas for PMK (pajamų mokesčio kreditas):
//
// 2018-2025: rate = 15%
//   - profit ≤ 20000: PMK = profit × 0.1
//   - profit > 20000: PMK = profit × (0.1 – 2/300000 × (profit – 20000))
//   - At 35000 EUR, credit becomes 0, effective rate = 15%
//
// 2026+: rate = 20%
//   - profit ≤ 20000: PMK = profit × 0.15
//   - profit > 20000 ≤ 42500: PMK = profit × (0.15 – 2/300000 × (profit – 20000))
//   - profit > 42500: No credit, 20% rate applies

// https://sodra.lt/imokos/vykdau-individualia-veikla
// Pasibaigus kalendoriniams metams, ne vėliau kaip iki gegužės 2 dienos, sumokamas skirtumas tarp sumokėtų ir mokėtinų PSD įmokų sumų.

/** TODO: Issue with calculator, we are double removing PSD and VAT when calculating
 * IF user adds expense records of VSD or PSD to taxes then we should not subtract them again
 */

export const VAT_RATE = 0.21
export const centsToEuros = (cents: number) => cents / 100

export const TAX_CONSTANTS_2025 = {
  GPM_RATE: 0.15,
  GPM_CREDIT_COEF_BASE: 0.10,
  GPM_CREDIT_COEF_FORMULA: 2/300000,
  GPM_LOWER_BOUND: 20000,
  GPM_UPPER_BOUND: 35000,
  MMA: 1038,
  PSD_MAX: 6329.59,
}

export const TAX_CONSTANTS_2026 = {
  GPM_RATE: 0.20,
  GPM_CREDIT_COEF_BASE: 0.15,
  GPM_CREDIT_COEF_FORMULA: 2/300000,
  GPM_LOWER_BOUND: 20000,
  GPM_UPPER_BOUND: 42500,
  MMA: 1153,
  PSD_MAX: 6939.69,
}

export const SODRA_CONSTANTS = {
  VSD_RATE: 0.1252,
  PSD_RATE: 0.0698,
  SODRA_BASE_MULTIPLIER: 0.9,
}

export const GPM_RULES_BY_YEAR = {
  2025: {
    constants: TAX_CONSTANTS_2025,
  },
  2026: {
    constants: TAX_CONSTANTS_2026,
  },
} as const

export interface GPMResult {
  amount: number
  credit: number
  effectiveRate: number
}

export interface TaxCalculation {
  gpmBase: number
  gpmTaxableProfit: number
  gpmCredit: number
  gpmAmount: number
  gpmEffectiveRate: number
  sodraBase: number
  vsdAmount: number
  psdPaid: number          // Annual monthly-floor advance: MMA × 6.98% × months (0 if floor waived)
  psdDifference: number    // PSD on profit due May 2: max(0, incomeBased - psdPaid)
  psdFloorWaived: boolean  // Monthly PSD floor waived (insurance_status set & != self_insured)
  vsdWaivedReason: 'status' | 'first_year' | null  // null = VSD charged (pensioner/first-year => waived)
  totalTax: number
  taxPercentage: number
  netEarnings: number
  netEarningsMonthly: number
}

export type TaxOptions = {
  method?: 'actual' | 'fixed_30'
  monthsActive?: number
  // Taxpayer profile — the user-derived waivers (see useTaxProfile + docs spec §3).
  insuranceStatus?: InsuranceStatus | null
  firstYearStartedAt?: string | null
}

export const round2 = (value: number) => Math.round(value * 100) / 100

type GpmYear = keyof typeof GPM_RULES_BY_YEAR

export const getGpmRule = (year: number) => {
  return GPM_RULES_BY_YEAR[year as GpmYear] ?? GPM_RULES_BY_YEAR[2025]
}

export const getGpmEffectiveRateInfo = (year: number, t: TFunction) => {
  const ruleYear = (year in GPM_RULES_BY_YEAR
    ? year
    : 2025) as GpmYear

  return t(`taxes.effectiveRateInfo${ruleYear}`)
}

export const calculateGPM = (profit: number, year: number = 2025): GPMResult => {
  if (profit <= 0) {
    return { amount: 0, credit: 0, effectiveRate: 0 }
  }

  const constants = getGpmRule(year).constants
  const grossTax = profit * constants.GPM_RATE

  const creditCoef = profit <= constants.GPM_LOWER_BOUND
    ? constants.GPM_CREDIT_COEF_BASE
    : Math.max(0, constants.GPM_CREDIT_COEF_BASE - constants.GPM_CREDIT_COEF_FORMULA * (profit - constants.GPM_LOWER_BOUND))

  const credit = profit * creditCoef
  const amount = grossTax - credit

  const roundedAmount = round2(amount)
  const roundedCredit = round2(credit)

  return {
    amount: roundedAmount,
    credit: roundedCredit,
    effectiveRate: profit > 0 ? roundedAmount / profit : 0
  }
}

export interface MonthlyTaxBreakdown {
  month: number
  income: number
  expenses: number
}

export interface MonthlyTaxResult {
  month: number
  incrementalGpm: number
  incrementalVsd: number
  incrementalPsd: number
}

/**
 * Mėnesinių GPM ir VSD mokėjimų skaičiavimas.
 * 
 * SVARBU: Mėnesiniai mokėjimai naudoja PROGRESINĮ monthsActive skaičiavimą:
 * - Kiekvienas mėnuo naudoja monthsActive = month - firstMonth + 1
 * - Sausis (pirmas mėnuo): monthsActive = 1, PSD = €72.45
 * - Vasaris: monthsActive = 2, PSD = €144.90
 * - ir t.t.
 * 
 * KODĖL PROGRESINIS:
 * - Mėnesiniai mokėjimai turi atspindėti FAKTIŠKAI sumokėtą PSD iki to mėnesio
 * - Jei naudotume pilną metinį PSD (12 mėn), pirmo mėnesio GPM būtų ~€0
 *   nes visa suma būtų atimta iš apmokestinamo pelno
 * - Progresinis metodas užtikrina, kad vairuotojas moka GPM proporcingai
 * 
 * METŲ PABAIGOJE:
 * - Dashboard naudoja pilną monthsActive = 12 - firstMonth + 1
 * - Mėnesinė suma bus DIDESNĖ nei metinė (nes mažiau PSD atimta)
 * - gpmDrift bus TEIGIAMAS = vairuotojas gaus grąžinimą iš VMI
 * 
 * Tai yra KONSERVATYVUS metodas: vairuotojas niekada neprimoka.
 */
export const calculateMonthlyTaxes = (
  monthsBreakdown: MonthlyTaxBreakdown[],
  year: number = 2025,
  // Same taxpayer profile as the yearly calc — passing it to both keeps the
  // monthly sums reconciled with the yearly total (no gpmDrift regression).
  profile: Pick<TaxOptions, 'insuranceStatus' | 'firstYearStartedAt'> = {}
): MonthlyTaxResult[] => {
  if (!monthsBreakdown.length) return []

  const sorted = [...monthsBreakdown].sort((a, b) => a.month - b.month)
  const firstMonth = sorted[0].month

  let runningIncome = 0
  let runningExpenses = 0
  let runningPaidGpm = 0
  let runningPaidVsd = 0
  let runningPaidPsd = 0

  return sorted.map(({ month, income, expenses }) => {
    runningIncome += centsToEuros(income)
    runningExpenses += centsToEuros(expenses)

    // Progressive monthsActive: count months from first activity to current month
    const monthsActive = month - firstMonth + 1
    const currentTaxes = calculateTaxes(runningIncome, runningExpenses, year, { monthsActive, ...profile })

    // Incremental = what we owe now - what we've already paid
    const incrementalGpm = Math.max(0, currentTaxes.gpmAmount - runningPaidGpm)
    const incrementalVsd = Math.max(0, currentTaxes.vsdAmount - runningPaidVsd)
    const incrementalPsd = Math.max(0, currentTaxes.psdPaid - runningPaidPsd)

    // Update running totals with what we're paying this month
    runningPaidGpm += incrementalGpm
    runningPaidVsd += incrementalVsd
    runningPaidPsd += incrementalPsd

    return {
      month,
      incrementalGpm,
      incrementalVsd,
      incrementalPsd,
    }
  })
}

/**
 * GPM nuokrypis (drift): skirtumas tarp mėnesinių GPM mokėjimų sumos ir metinio GPM.
 * 
 * Formulė: monthlySum - yearlyGPM
 * - Teigiamas = vairuotojas permokėjo, gaus grąžinimą iš VMI (grąžinimas)
 * - Neigiamas = vairuotojas neprimokėjo (trūkumas) - NIEKADA NEĮVYKSTA
 * 
 * KODĖL TRŪKUMAS NEĮMANOMAS:
 * 
 * 1. Mėnesiniai GPM skaičiuojami naudojant Math.max(0, ...), kas reiškia, kad
 *    neigiami prieaugiai (kai išlaidos sumažina GPM) yra apkarpomi iki 0.
 *    Todėl mėnesinė suma visada >= metinė suma.
 * 
 * 2. Pavyzdys: vairuotojas uždirba sausio-lapkričio mėnesiais, bet gruodžio
 *    mėnesį prideda tik išlaidas (komisinį). Gruodžio išlaidos sumažina metinį
 *    GPM, bet mėnesinis GPM prieaugis gruodžiui = max(0, mažesnis - didesnis) = 0.
 *    Sumažinimas "prarandamas" - mėnesinė suma lieka didesnė nei metinė.
 * 
 * 3. Backend visada grąžina months_active = 12 - firstIncomeMonth + 1, kas
 *    atitinka totalMonthsActive skaičiuojamą calculateMonthlyTaxes funkcijoje.
 *    Todėl PSD skaičiavimai sutampa ir nesukuria papildomo nuokrypio.
 * 
 * Rezultatas: gpmDrift visada >= 0, todėl rodome tik grąžinimo atvejį.
 */
export const calculateGpmDrift = (
  yearlyGpmAmount: number,
  monthlyTaxResults: MonthlyTaxResult[]
): number => {
  const monthlySum = monthlyTaxResults.reduce((sum, m) => sum + m.incrementalGpm, 0)
  return round2(monthlySum - yearlyGpmAmount)
}

export const calculateTaxes = (
  totalIncome: number,
  totalExpenses: number,
  year: number = 2025,
  options: TaxOptions
): TaxCalculation => {
  const constants = getGpmRule(year).constants
  const gpmBase = round2(Math.max(0, totalIncome - totalExpenses))

  const sodraBase = round2(gpmBase * SODRA_CONSTANTS.SODRA_BASE_MULTIPLIER)

  // --- Taxpayer-profile waivers (docs spec §3). Unset => max burden (= today). ---
  // The monthly PSD floor is an advance paid only by the self-insured.
  const psdFloorWaived = !!options.insuranceStatus && options.insuranceStatus !== 'self_insured'
  // A 40 % participation level ("dalyvumo lygis") waives PSD in full — the
  // monthly floor and the year-end PSD from profit (docs spec §3).
  const psdWaivedByStatus = options.insuranceStatus === 'participation_level_40'
  // VSD drops to 0 for pensioners, or for the first activity year (opt-out).
  const vsdWaivedByStatus = options.insuranceStatus === 'old_age_pension'
    || options.insuranceStatus === 'disability_pension'
    || options.insuranceStatus === 'participation_level_40'
  const vsdWaivedFirstYear = !!options.firstYearStartedAt
    && new Date(options.firstYearStartedAt).getFullYear() === year
  const vsdWaived = vsdWaivedByStatus || vsdWaivedFirstYear
  const vsdWaivedReason: TaxCalculation['vsdWaivedReason'] =
    vsdWaivedByStatus ? 'status' : vsdWaivedFirstYear ? 'first_year' : null

  const vsdAmount = vsdWaived ? 0 : round2(sodraBase * SODRA_CONSTANTS.VSD_RATE)

  const psdMonthly = round2(constants.MMA * SODRA_CONSTANTS.PSD_RATE)
  const psdPaid = psdFloorWaived ? 0 : round2(psdMonthly * (options.monthsActive ?? 12))
  const psdIncomeBased = psdWaivedByStatus ? 0 : round2(sodraBase * SODRA_CONSTANTS.PSD_RATE)
  const psdCapped = round2(Math.min(psdIncomeBased, constants.PSD_MAX))
  const psdDifference = round2(Math.max(0, psdCapped - psdPaid))

  let gpmTaxableProfit: number

  switch (options.method) {
    case 'fixed_30':
      gpmTaxableProfit = round2(gpmBase)
      break
    case 'actual':
    default:
      gpmTaxableProfit = round2(Math.max(0, gpmBase - vsdAmount - psdPaid - psdDifference))
      break
  }

  const gpm = calculateGPM(gpmTaxableProfit, year)

  const totalTax = round2(gpm.amount + vsdAmount + psdPaid + psdDifference)
  const taxPercentage = totalIncome > 0 ? (totalTax / totalIncome) * 100 : 0
  const netEarnings = round2(totalIncome - totalExpenses - totalTax)
  const netEarningsMonthly = round2(netEarnings / 12)

  return {
    gpmBase,
    gpmTaxableProfit,
    gpmCredit: gpm.credit,
    gpmAmount: gpm.amount,
    gpmEffectiveRate: gpm.effectiveRate,
    sodraBase,
    vsdAmount,
    psdPaid,
    psdDifference,
    psdFloorWaived,
    vsdWaivedReason,
    totalTax,
    taxPercentage,
    netEarnings,
    netEarningsMonthly,
  }
}

